import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../swof/api";

export const knopStijl = {
  fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
  color: "var(--gold)", border: "1px solid rgba(201,169,97,0.4)", padding: "10px 16px",
  background: "transparent",
};

export const wegStijl = {
  fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase",
  color: "#e8b0a4", border: "1px solid rgba(201,80,60,0.4)", padding: "8px 10px",
  background: "transparent",
};

export const invoerStijl = {
  width: "100%", padding: "12px 14px", background: "rgba(243,234,215,0.04)",
  border: "1px solid var(--line)", fontFamily: "var(--serif)", fontSize: 16,
  color: "var(--cream)", outline: "none",
};

export function Kopje({ children }) {
  return (
    <span className="eyebrow" style={{ display: "block", marginBottom: 8, fontSize: 10, color: "var(--gold)" }}>
      {children}
    </span>
  );
}

export function Foutmelding({ fout, testId = "admin-fout" }) {
  if (!fout) return null;
  return (
    <div data-testid={testId} style={{
      margin: "16px 0", padding: "12px 14px", border: "1px solid rgba(201,80,60,0.5)",
      background: "rgba(201,80,60,0.08)", fontFamily: "var(--sans)", fontSize: 13, color: "#e8b0a4",
    }}>{fout}</div>
  );
}

export function Titel({ children, onder }) {
  return (
    <>
      <h1 style={{
        fontFamily: "var(--serif)", fontSize: "clamp(30px, 3.6vw, 48px)", fontWeight: 400,
        margin: "0 0 12px", letterSpacing: "-0.02em",
      }}>{children}</h1>
      {onder && (
        <p style={{ fontFamily: "var(--sans)", fontSize: 14, opacity: 0.62, margin: "0 0 32px", maxWidth: "66ch" }}>
          {onder}
        </p>
      )}
    </>
  );
}

export function Duim({ media, hoogte = 64, ratio = "3 / 4" }) {
  return (
    <div style={{
      width: hoogte * 0.78, aspectRatio: ratio, background: "#1a1611",
      border: "1px solid var(--line)", overflow: "hidden", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {media ? (
        <img src={media.varianten?.["400"] || media.url} alt={media.alt || ""}
          style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <span style={{ fontFamily: "var(--mono)", fontSize: 8, color: "rgba(243,234,215,0.35)" }}>geen</span>
      )}
    </div>
  );
}

/** Uploadknop: kiest bestanden, vraagt alt-tekst, uploadt en geeft de nieuwe media terug. */
export function Uploader({ soort = "sfeerfoto", meerdere = true, label = "Foto's uploaden", onKlaar, testId }) {
  const invoer = useRef(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");

  const kies = async (e) => {
    const bestanden = [...e.target.files];
    if (!bestanden.length) return;
    const alt = window.prompt("Alt-tekst (verplicht) — beschrijf kort wat er te zien is:", "");
    if (!alt || !alt.trim()) {
      setFout("Zonder alt-tekst kan de foto niet worden geüpload.");
      invoer.current.value = "";
      return;
    }
    const form = new FormData();
    bestanden.forEach((b) => form.append("bestanden", b));
    form.append("alt", alt);
    form.append("soort", soort);
    setBezig(true);
    setFout("");
    try {
      const { data } = await api.post("/admin/media", form);
      onKlaar(data);
    } catch (err) {
      setFout(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBezig(false);
      invoer.current.value = "";
    }
  };

  return (
    <>
      <button onClick={() => invoer.current.click()} disabled={bezig} data-testid={testId} style={knopStijl}>
        {bezig ? "Uploaden…" : label}
      </button>
      <input ref={invoer} type="file" accept="image/jpeg,image/png,image/webp" multiple={meerdere}
        onChange={kies} style={{ display: "none" }} data-testid={`${testId}-input`} />
      <Foutmelding fout={fout} testId={`${testId}-fout`} />
    </>
  );
}

/** Haalt /admin/site op en geeft {content, herlaad, fout, setFout}. */
export function useAdminContent() {
  const [content, setContent] = useState(null);
  const [fout, setFout] = useState("");
  const herlaad = () => api.get("/admin/site")
    .then(({ data }) => setContent(data))
    .catch((e) => setFout(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  useEffect(() => {
    document.body.classList.add("swof");
    herlaad();
    return () => document.body.classList.remove("swof");
  }, []);
  const melden = (e) => setFout(formatApiErrorDetail(e.response?.data?.detail) || e.message);
  return { content, herlaad, fout, setFout, melden };
}

export function mediaKaart(media) {
  return (media || []).reduce((acc, m) => { acc[m.id] = m; return acc; }, {});
}

/** Sleepbare lijst: geeft per item de render-functie met een handvat. */
export function Sleeplijst({ items, onOrde, render, testId }) {
  const [sleep, setSleep] = useState(null);
  const laatVallen = (naar) => {
    if (sleep === null || sleep === naar) return setSleep(null);
    const nieuw = [...items];
    const [verplaatst] = nieuw.splice(sleep, 1);
    nieuw.splice(naar, 0, verplaatst);
    setSleep(null);
    onOrde(nieuw);
  };
  return (
    <ul data-testid={testId} style={{ listStyle: "none", padding: 0, margin: 0, borderTop: "1px solid var(--line)" }}>
      {items.map((item, i) => (
        <li key={item.id} draggable onDragStart={() => setSleep(i)}
          onDragOver={(e) => e.preventDefault()} onDrop={() => laatVallen(i)}
          onDragEnd={() => setSleep(null)}
          style={{
            display: "flex", alignItems: "center", gap: 16, padding: "12px 8px",
            borderBottom: "1px solid var(--line)",
            background: sleep === i ? "rgba(201,169,97,0.08)" : "transparent",
          }}>
          <span title="Sleep om te herordenen" style={{
            cursor: "grab", color: "var(--gold)", opacity: 0.6, userSelect: "none",
          }}>⠿</span>
          <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--gold)", width: 26 }}>
            {String(i + 1).padStart(2, "0")}
          </span>
          {render(item, i)}
        </li>
      ))}
    </ul>
  );
}
